import React, { useState, useEffect } from 'react';
import { employeeApi } from '../services/hrApi';
import type { Employee } from '../types';
import './PageStyles.scss';
import './Employees.scss';

type ViewMode = 'grid' | 'list';

const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'terminated', label: 'Terminated' },
  ];

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [searchQuery, statusFilter, employees]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await employeeApi.getEmployees();
      // Handle both array response and object with results array
      const employeesList = Array.isArray(data) ? data : (data.results || data.employees || []);
      setEmployees(employeesList);
      setFilteredEmployees(employeesList);
    } catch (err: any) {
      console.error('Error fetching employees:', err);
      setError(err?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter((employee) => employee.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((employee) => {
        const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
        const employeeId = employee.employee_id?.toLowerCase() || '';
        const email = employee.email?.toLowerCase() || '';
        const department = employee.department?.toLowerCase() || '';
        const position = employee.position?.toLowerCase() || '';

        return (
          fullName.includes(query) ||
          employeeId.includes(query) ||
          email.includes(query) ||
          department.includes(query) ||
          position.includes(query)
        );
      });
    }

    setFilteredEmployees(filtered);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'onboarding':
        return 'status-badge status-onboarding';
      case 'inactive':
        return 'status-badge status-inactive';
      case 'terminated':
        return 'status-badge status-terminated';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="hr-portal-page">
      <div className="hr-portal-page-header">
        <div className="page-header-top">
          <div>
            <h2>Employees</h2>
            <p className="page-description">Manage employee information and profiles</p>
          </div>
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <i className="bx bx-grid-alt"></i>
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <i className="bx bx-list-ul"></i>
            </button>
          </div>
        </div>
      </div>
      <div className="hr-portal-page-content">
        {!loading && !error && employees.length > 0 && (
          <div className="employees-filters-container">
            <div className="filters-row">
              <div className="filter-group">
                <label htmlFor="status-filter">Status</label>
                <select
                  id="status-filter"
                  className="status-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="search-input-wrapper">
                <i className="bx bx-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name, employee ID, email, department, or position..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <i className="bx bx-x"></i>
                  </button>
                )}
              </div>
            </div>
            {(searchQuery || statusFilter) && (
              <div className="search-results-info">
                Showing {filteredEmployees.length} of {employees.length} employees
                {statusFilter && (
                  <span className="active-filter-tag">
                    Status: {statusOptions.find(opt => opt.value === statusFilter)?.label}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="employees-loading">
            <p>Loading employees...</p>
          </div>
        )}

        {error && (
          <div className="employees-error">
            <p>{error}</p>
            <button onClick={fetchEmployees} className="btn-retry">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && employees.length === 0 && (
          <div className="employees-empty">
            <i className="bx bx-user"></i>
            <p>No employees found</p>
          </div>
        )}

        {!loading && !error && employees.length > 0 && filteredEmployees.length === 0 && (searchQuery || statusFilter) && (
          <div className="employees-empty">
            <i className="bx bx-search"></i>
            <p>No employees match your filter criteria</p>
            <button 
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('active');
              }} 
              className="btn-clear-search"
            >
              Clear Filters
            </button>
          </div>
        )}

        {!loading && !error && filteredEmployees.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <div className="employees-grid">
                {filteredEmployees.map((employee) => (
                  <div key={employee.id} className="employee-card">
                    <div className="employee-card-header">
                      <div className="employee-avatar">
                        {employee.first_name?.[0]?.toUpperCase() || 'E'}
                        {employee.last_name?.[0]?.toUpperCase() || ''}
                      </div>
                      <span className={getStatusBadgeClass(employee.status)}>
                        {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                      </span>
                    </div>
                    <div className="employee-card-body">
                      <h3 className="employee-name">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <p className="employee-id">ID: {employee.employee_id}</p>
                      <div className="employee-info">
                        <div className="info-item">
                          <i className="bx bx-envelope"></i>
                          <span>{employee.email}</span>
                        </div>
                        {employee.phone && (
                          <div className="info-item">
                            <i className="bx bx-phone"></i>
                            <span>{employee.phone}</span>
                          </div>
                        )}
                        <div className="info-item">
                          <i className="bx bx-briefcase"></i>
                          <span>{employee.position}</span>
                        </div>
                        <div className="info-item">
                          <i className="bx bx-building"></i>
                          <span>{employee.department}</span>
                        </div>
                        <div className="info-item">
                          <i className="bx bx-calendar"></i>
                          <span>Hired: {formatDate(employee.hire_date)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="employees-table-container">
                <table className="employees-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Employee ID</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Position</th>
                      <th>Status</th>
                      <th>Hire Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((employee) => (
                      <tr key={employee.id}>
                        <td>
                          <div className="employee-list-name">
                            <div className="employee-avatar-small">
                              {employee.first_name?.[0]?.toUpperCase() || 'E'}
                              {employee.last_name?.[0]?.toUpperCase() || ''}
                            </div>
                            <span>
                              {employee.first_name} {employee.last_name}
                            </span>
                          </div>
                        </td>
                        <td>{employee.employee_id}</td>
                        <td>{employee.email}</td>
                        <td>{employee.department}</td>
                        <td>{employee.position}</td>
                        <td>
                          <span className={getStatusBadgeClass(employee.status)}>
                            {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                          </span>
                        </td>
                        <td>{formatDate(employee.hire_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Employees;
